import React, { useEffect, useState } from "react";
import { ArrowLeft, Grid2x2, Play, Square, RefreshCw, Settings as SettingsIcon } from "lucide-react";
import { Toaster, toast } from "sonner";
import {
    fetchGridStatus, fetchGridSnapshot, fetchGridOrders, fetchGridTrades,
    fetchGridPriceHistory, startGrid, stopGrid, fetchGridConfig, updateGridConfig,
} from "@/lib/api";
import GridLadder from "@/components/GridLadder";
import PriceChart from "@/components/PriceChart";
import SettingsDialog from "@/components/SettingsDialog";
import { OpenOrdersTable, TradeHistoryTable } from "@/components/OrdersTables";

const C = {
    bg: "#0B0F14", panel: "#0F1620", border: "#1B2A3A", text: "#E6EDF6",
    sub: "#A7B4C6", dim: "#6F7D92", accent: "#2EE6D6", bull: "#2BE38B", bear: "#FF5C6C",
};

/**
 * Dedicated dashboard for ONE multi-grid instance (#/grids/{gid}) — the same
 * ladder + live chart + orders view the primary dashboard has, scoped to the
 * grid's own runtime, symbol and database.
 */
export default function GridDashboardPage({ gid, onBack }) {
    const [status, setStatus] = useState(null);
    const [snap, setSnap] = useState(null);
    const [orders, setOrders] = useState([]);
    const [trades, setTrades] = useState([]);
    const [priceHistory, setPriceHistory] = useState([]);
    const [missing, setMissing] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [config, setConfig] = useState(null);

    const openSettings = async () => {
        try { setConfig(await fetchGridConfig(gid)); } catch (_) { /* transient */ }
        setSettingsOpen(true);
    };

    const onSaveSettings = async (cfg) => {
        try {
            await updateGridConfig(gid, cfg);
            setConfig(await fetchGridConfig(gid));
            setSettingsOpen(false);
            toast.success("Grid settings saved", { description: "Applies on the next tick; persists across restarts." });
        } catch (e) {
            toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
        }
    };

    useEffect(() => {
        let alive = true;
        const tick = async () => {
            try {
                const [s, g, ph] = await Promise.all([
                    fetchGridStatus(gid), fetchGridSnapshot(gid), fetchGridPriceHistory(gid),
                ]);
                if (!alive) return;
                setStatus(s); setSnap(g); setPriceHistory(ph?.items || []);
                setMissing(false);
            } catch (e) {
                if (alive && e?.response?.status === 404) setMissing(true);
            }
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => { alive = false; clearInterval(id); };
    }, [gid]);

    useEffect(() => {
        let alive = true;
        const tick = async () => {
            try {
                const [o, t] = await Promise.all([fetchGridOrders(gid, "open"), fetchGridTrades(gid, 200)]);
                if (!alive) return;
                setOrders(o?.items || []); setTrades(t?.items || []);
            } catch (_) { /* transient */ }
        };
        tick();
        const id = setInterval(tick, 2500);
        return () => { alive = false; clearInterval(id); };
    }, [gid]);

    const symbol = status?.config?.symbol || snap?.symbol || "…";
    const running = !!status?.running;
    const live = status?.mode === "live";
    const price = status?.last_price || snap?.last_price || null;

    const toggleRun = async () => {
        try {
            if (running) { await stopGrid(gid); toast(`${symbol} grid stopped`); }
            else { await startGrid(gid, status?.mode); toast.success(`${symbol} grid started`); }
        } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    };

    return (
        <div className="min-h-screen" style={{ background: C.bg, color: C.text }} data-testid="grid-dashboard-page">
            <Toaster position="bottom-right" toastOptions={{ style: { background: C.panel, border: `1px solid ${C.border}`, color: C.text } }} />
            <div className="sticky top-0 z-40 backdrop-blur-md" style={{ background: "rgba(11,15,20,0.85)", borderBottom: `1px solid ${C.border}` }}>
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-3 flex items-center gap-3 flex-wrap">
                    <button onClick={onBack} className="btn-anim flex items-center gap-1.5 text-sm" style={{ color: C.sub }} data-testid="grid-dash-back">
                        <ArrowLeft size={16} /> All Grids
                    </button>
                    <div className="h-6 w-px" style={{ background: C.border }} />
                    <Grid2x2 size={16} color={C.accent} />
                    <span className="text-sm font-semibold font-mono">{symbol}</span>
                    <span className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-[0.1em] font-mono"
                        style={running ? { background: "rgba(43,227,139,0.12)", color: C.bull } : { background: "#13202D", color: C.dim }}>
                        {running ? "running" : "stopped"}
                    </span>
                    <span className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-[0.1em] font-mono"
                        style={live ? { background: "rgba(255,92,108,0.12)", color: C.bear } : { background: "rgba(46,230,214,0.1)", color: C.accent }}>
                        {live ? "LIVE" : "PAPER"}
                    </span>
                    <span className="text-[10px] font-mono" style={{ color: C.dim }}>#{gid}</span>
                    <div className="flex-1" />
                    {price != null && (
                        <span className="font-mono text-sm" style={{ color: C.accent }}>
                            ${Number(price).toLocaleString("en-US", { maximumFractionDigits: 6 })}
                        </span>
                    )}
                    <button onClick={openSettings}
                        className="btn-anim flex items-center gap-1.5 h-8 px-2.5 rounded text-xs"
                        style={{ border: `1px solid ${C.border}`, color: C.sub }}
                        data-testid="grid-dash-settings">
                        <SettingsIcon size={12} /> Settings
                    </button>
                    <button onClick={toggleRun}
                        className="btn-anim flex items-center gap-1.5 h-8 px-3 rounded text-xs font-medium"
                        style={running ? { background: C.bear, color: C.bg } : { background: C.bull, color: C.bg }}
                        data-testid="grid-dash-run-toggle">
                        {running ? <Square size={12} /> : <Play size={12} />} {running ? "Stop" : "Start"}
                    </button>
                </div>
            </div>

            <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-5">
                {missing ? (
                    <div className="panel p-8 text-center text-sm" style={{ color: C.bear }}>
                        Grid #{gid} not found — it may have been deleted.
                        <button onClick={onBack} className="btn-anim block mx-auto mt-3 text-xs underline" style={{ color: C.sub }}>Back to grids</button>
                    </div>
                ) : (
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-12 lg:col-span-7">
                            <GridLadder
                                price={price}
                                gridSize={status?.config?.grid_size || snap?.grid_size || 250}
                                buys={snap?.active_buys || []}
                                sells={snap?.active_sells || []}
                                mode={status?.mode || "paper"}
                                symbol={symbol}
                            />
                        </div>
                        <div className="col-span-12 lg:col-span-5 flex flex-col gap-4">
                            <PriceChart
                                data={priceHistory}
                                buys={snap?.active_buys || []}
                                sells={snap?.active_sells || []}
                                symbol={symbol}
                            />
                            <div className="panel p-4" data-testid="grid-dash-stats">
                                <div className="text-[10px] uppercase tracking-[0.18em] mb-3" style={{ color: C.sub }}>Grid Stats</div>
                                <div className="grid grid-cols-2 gap-3 text-center">
                                    {[["Realized PnL", `$${Number(snap?.realized_pnl ?? 0).toFixed(4)}`, (snap?.realized_pnl ?? 0) >= 0 ? C.bull : C.bear],
                                      ["Cycles", snap?.completed_cycles ?? 0, C.text],
                                      ["Active BUYs", (snap?.active_buys || []).length, C.bull],
                                      ["Active SELLs", (snap?.active_sells || []).length, C.bear]].map(([l, v, c]) => (
                                        <div key={l} className="rounded p-2" style={{ background: "#0B1219" }}>
                                            <div className="text-[9px] uppercase tracking-[0.1em]" style={{ color: C.dim }}>{l}</div>
                                            <div className="font-mono text-lg" style={{ color: c }}>{v}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="col-span-12 lg:col-span-6">
                            <OpenOrdersTable orders={orders} onGridClick={() => {}} />
                        </div>
                        <div className="col-span-12 lg:col-span-6">
                            <TradeHistoryTable trades={trades} onGridClick={() => {}} />
                        </div>
                    </div>
                )}
            </div>
            <SettingsDialog
                open={settingsOpen}
                onOpenChange={setSettingsOpen}
                initial={config || status?.config || {}}
                autoSizing={config?.auto_sizing || null}
                walletAllocation={status?.wallet_allocation || null}
                onSave={onSaveSettings}
            />
        </div>
    );
}
